import { useCallback, useEffect, useState } from "react";
import {
  GROOMING_RUN_PHASES,
  GROOMING_TYPICAL_RUN_FALLBACK,
  type GroomingRunnerEvent,
  type GroomingRunPhase,
  type GroomingToolsetBridge,
} from "@ttc-spectree/toolbox-contract";
import { useRunnerEvents } from "@/lib/grooming/runner-bridge";
import { useToolboxBridge } from "@/lib/toolbox/bridge";
import { cn } from "@/lib/utils";
import { TOOLBOX_TEXT } from "@/lib/toolbox/type-scale";

/**
 * Live run activity (epic PI-SPEC-SW-E280, feature PI-SPEC-SW-1403; Claude
 * Design "SpecTree Grooming Desktop", One item phases and the Workspace's
 * running item; design delta section 4 item 5, "option B").
 *
 * A contract-2 SpecTree Toolbox reports `item-progress` events: the phase of
 * one item's run, the run's start time and one plain activity line. This
 * shows the three phases (done, now, next), a ticking elapsed timer with a
 * typical-time hint ("2:41 · usually 3–5 min") and the single activity line,
 * replaced as events arrive (no history is kept). A contract-1 app never
 * sends progress, so the page shows a spinner and "Claude is reading the
 * code…" only.
 */

/** Plain names of the run phases, in the engine's order. */
export const RUN_PHASE_LABELS: Readonly<Record<GroomingRunPhase, string>> = {
  "updating-repos": "Updating repo copies",
  "reading-code": "Reading the code",
  "writing-brief": "Writing the brief",
};

/** The text a contract-1 app (or a browser watching a run) shows while an item runs. */
export const RUN_FALLBACK_TEXT = "Claude is reading the code…";

/** The latest progress the page heard for one item's run. */
export interface RunProgress {
  phase: GroomingRunPhase;
  /** The current activity line; null until one arrives. */
  activity: string | null;
  /** When the run started, ISO 8601. */
  startedAt: string;
  batchId: string;
  batchItemId?: string;
  itemId?: string;
}

/** Which run's progress to follow. Any id left out is not checked. */
export interface RunProgressMatch {
  batchId?: string | null | undefined;
  batchItemId?: string | null | undefined;
  itemId?: string | null | undefined;
}

type ProgressEvent = Extract<GroomingRunnerEvent, { type: "item-progress" }>;

/**
 * Whether a progress event belongs to the run the page follows: the batch
 * item when both name one, else the tracker item when both name one, else
 * the batch. Pure; exported for tests.
 */
export function progressMatches(event: ProgressEvent, match: RunProgressMatch): boolean {
  if (match.batchId && event.batchId !== match.batchId) return false;
  if (match.batchItemId && event.batchItemId) return event.batchItemId === match.batchItemId;
  if (match.itemId && event.itemId) return event.itemId === match.itemId;
  if (match.batchItemId || match.itemId) return Boolean(match.batchId) && !event.batchItemId && !event.itemId;
  return Boolean(match.batchId);
}

/** Fold one progress event into the previous state: the line is replaced, never appended. Pure. */
export function nextProgress(previous: RunProgress | null, event: ProgressEvent): RunProgress {
  const sameRun =
    previous !== null &&
    previous.batchId === event.batchId &&
    (previous.batchItemId ?? previous.itemId) === (event.batchItemId ?? event.itemId);
  const keepLine = sameRun && previous.phase === event.phase;
  return {
    phase: event.phase,
    activity: event.activity ?? (keepLine ? previous.activity : null),
    startedAt: event.startedAt,
    batchId: event.batchId,
    ...(event.batchItemId ? { batchItemId: event.batchItemId } : {}),
    ...(event.itemId ? { itemId: event.itemId } : {}),
  };
}

function matchKey(match: RunProgressMatch): string {
  return `${match.batchId ?? ""}|${match.batchItemId ?? ""}|${match.itemId ?? ""}`;
}

/**
 * The latest `item-progress` of the run `match` names, or null before the
 * first one (and always with a contract-1 app, which sends none). Changing
 * `match` starts over.
 */
export function useRunProgress(runner: GroomingToolsetBridge | null, match: RunProgressMatch): RunProgress | null {
  const key = matchKey(match);
  const [state, setState] = useState<{ key: string; progress: RunProgress | null }>({ key, progress: null });
  const { batchId, batchItemId, itemId } = match;

  const onEvent = useCallback(
    (event: GroomingRunnerEvent) => {
      if (event.type !== "item-progress") return;
      if (!progressMatches(event, { batchId, batchItemId, itemId })) return;
      setState((prev) => ({ key, progress: nextProgress(prev.key === key ? prev.progress : null, event) }));
    },
    [key, batchId, batchItemId, itemId]
  );
  useRunnerEvents(runner, onEvent);

  return state.key === key ? state.progress : null;
}

/** `161` seconds → "2:41"; an hour or more → "1:02:03". */
export function formatElapsed(totalSeconds: number): string {
  const seconds = Math.max(0, Math.floor(totalSeconds));
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  const ss = String(s).padStart(2, "0");
  return h > 0 ? `${String(h)}:${String(m).padStart(2, "0")}:${ss}` : `${String(m)}:${ss}`;
}

/**
 * "usually X–Y min" from the typical run time (±25%, rounded to whole
 * minutes, at least one minute apart), or the fallback when there is no
 * history.
 */
export function typicalRunHint(typicalItemSeconds: number | null | undefined): string {
  if (typicalItemSeconds === null || typicalItemSeconds === undefined || !(typicalItemSeconds > 0)) {
    return GROOMING_TYPICAL_RUN_FALLBACK;
  }
  const low = Math.max(1, Math.round((typicalItemSeconds * 0.75) / 60));
  let high = Math.round((typicalItemSeconds * 1.25) / 60);
  if (high <= low) high = low + 1;
  return `usually ${String(low)}–${String(high)} min`;
}

/** Seconds since `startedAt`, ticking once a second while mounted. */
function useElapsedSeconds(startedAt: string | number): number {
  const start = typeof startedAt === "number" ? startedAt : Date.parse(startedAt);
  const [now, setNow] = useState(Date.now);
  useEffect(() => {
    const timer = setInterval(() => {
      setNow(Date.now());
    }, 1000);
    return () => {
      clearInterval(timer);
    };
  }, []);
  return Number.isNaN(start) ? 0 : Math.max(0, (now - start) / 1000);
}

/** True when the app sends `item-progress` (grooming contract 2 or later). */
export function useSupportsRunProgress(): boolean {
  const bridge = useToolboxBridge();
  return (bridge?.app.contracts.grooming ?? 1) >= 2;
}

function Spinner({ className }: { className?: string }) {
  return (
    <span
      className={cn(
        "inline-block h-[18px] w-[18px] shrink-0 animate-spin rounded-full border-2 border-primary border-r-transparent",
        className
      )}
      aria-hidden="true"
    />
  );
}

type PhaseMark = "done" | "now" | "next";

function Mark({ mark }: { mark: PhaseMark }) {
  if (mark === "done") {
    return (
      <span
        className="flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-full bg-green-500/15 text-[11px] font-bold text-green-400"
        aria-hidden="true"
      >
        ✓
      </span>
    );
  }
  if (mark === "now") return <Spinner />;
  return <span className="h-[18px] w-[18px] shrink-0 rounded-full border border-dashed border-zinc-600" aria-hidden="true" />;
}

export interface RunActivityProps {
  /** The latest progress (from {@link useRunProgress}); null before the first event. */
  progress: RunProgress | null;
  /** When the page started the run, used for the timer until the first event says otherwise. */
  startedAt?: string | number | null;
  /** Median recent brief run in seconds, for the hint; null or absent shows the fallback. */
  typicalItemSeconds?: number | null;
  /** False for a contract-1 app (or a browser): spinner and fallback text only. */
  supportsProgress: boolean;
  /** One quiet line under the activity, for example "You can review and post finished items while this runs." */
  note?: string;
  className?: string;
}

/** The three phases with done/now/next marks, the elapsed timer, the typical-time hint and the live line. */
export function RunActivity({ progress, startedAt, typicalItemSeconds, supportsProgress, note, className }: RunActivityProps) {
  const [mountedAt] = useState(() => Date.now());
  const start = progress?.startedAt ?? startedAt ?? mountedAt;
  const elapsed = useElapsedSeconds(start);

  if (!supportsProgress) {
    return (
      <div className={cn("glass-card flex flex-col gap-3 rounded-[10px] px-5 py-[18px]", className)} role="status" data-testid="run-activity" data-mode="fallback">
        <p className="flex items-center gap-2 font-medium">
          <Spinner className="h-3.5 w-3.5" />
          {RUN_FALLBACK_TEXT}
        </p>
        <span className="h-3 w-full rounded-md bg-gradient-to-r from-[#111113] via-[#232328] to-[#111113] motion-safe:animate-pulse" aria-hidden="true" />
        <span className="h-3 w-[82%] rounded-md bg-gradient-to-r from-[#111113] via-[#232328] to-[#111113] motion-safe:animate-pulse" aria-hidden="true" />
        {note && <p className={cn("text-[13px]", TOOLBOX_TEXT.faint)}>{note}</p>}
      </div>
    );
  }

  const current = GROOMING_RUN_PHASES.indexOf(progress?.phase ?? GROOMING_RUN_PHASES[0]);
  return (
    <div className={cn("glass-card flex flex-col rounded-[10px] py-2", className)} role="status" aria-live="polite" data-testid="run-activity" data-mode="live">
      <ol aria-label="Run phases">
        {GROOMING_RUN_PHASES.map((phase, index) => {
          const mark: PhaseMark = index < current ? "done" : index === current ? "now" : "next";
          return (
            <li key={phase} className="flex items-start gap-3 px-5 py-3" data-testid="run-phase" data-phase={phase} data-mark={mark}>
              <span className="mt-px">
                <Mark mark={mark} />
              </span>
              <span className="flex min-w-0 flex-col gap-1">
                <span className={cn("text-sm", mark === "next" ? TOOLBOX_TEXT.faint : "text-foreground", mark === "now" && "font-medium")}>
                  {RUN_PHASE_LABELS[phase]}
                </span>
                {mark === "now" && (
                  <>
                    <span className="font-mono text-xs text-muted-foreground" data-testid="run-elapsed">
                      {formatElapsed(elapsed)} · {typicalRunHint(typicalItemSeconds)}
                    </span>
                    {progress?.activity && (
                      <span className="truncate text-sm text-muted-foreground" data-testid="run-activity-line">
                        {progress.activity}
                      </span>
                    )}
                  </>
                )}
              </span>
            </li>
          );
        })}
      </ol>
      {note && <p className={cn("px-5 pb-2 text-[13px]", TOOLBOX_TEXT.faint)}>{note}</p>}
    </div>
  );
}
